import { computed, getCurrentInstance } from 'vue';
import { SprintState } from '@superthread-com/api';
import { convertTimestampToDateSecondFormat } from '@/utilities/timeDifference';
import useMoveToSprint from '@/utilities/composables/useMoveToSprint';
import useSprintStateIcon from '@/utilities/composables/useSprintStateIcon';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import useTranslate from '@/utilities/composables/useTranslate';

const stateOrder: { [index: string]: any } = {
  active: 1,
  planned: 2,
  completed: 3,
  canceled: 4,
};

export default function useSprintPicker(card: any) {
  const _this = getCurrentInstance()!.proxy as any;
  if (!_this) return;

  const { getStatusLabelText } = useMoveToSprint() || {};
  const { sprintStateIcon } = useSprintStateIcon();
  const { iconPath } = useCustomIcon();
  const { translate } = useTranslate();

  const generateSprintMeta = (sprint: any, foundFirstPlannedSprint: any) => {
    const startDate = convertTimestampToDateSecondFormat(sprint.start_date);
    const endDate = convertTimestampToDateSecondFormat(sprint.end_date);
    const sprintDateRange = `${startDate}-${endDate}`;
    const sprintStatusText = getStatusLabelText?.(sprint.state);
    switch (sprint.state) {
      case SprintState.Completed:
        return sprintStatusText;
      case SprintState.Active:
      case SprintState.Canceled:
        return `${sprintStatusText} (${sprintDateRange})`;
      case SprintState.Planned:
        if (foundFirstPlannedSprint) {
          return sprintDateRange;
        }
        return `${sprintStatusText} (${sprintDateRange})`;
      default:
        return '';
    }
  };

  const spaceSprints = computed(() => {
    return Object.values(_this.$store.getters.getSpaceSprints(card.value.project_id) || {});
  });

  const sortedSpaceSprints = computed(() => {
    const sortedSprints = [...spaceSprints.value];
    sortedSprints.sort((a: any, b: any) => stateOrder[a.state] - stateOrder[b.state]);

    return sortedSprints;
  });

  const sprintOptions = computed(() => {
    let foundFirstPlannedSprint = false;
    const sprints = sortedSpaceSprints.value.map((sprint: any) => {
      const sprintTitle = sprint.title;
      const meta = generateSprintMeta(sprint, foundFirstPlannedSprint);

      if (sprint.state === SprintState.Planned && !foundFirstPlannedSprint) {
        foundFirstPlannedSprint = true;
      }

      return {
        id: sprint.id,
        label: sprintTitle,
        meta,
        title: sprintTitle,
        icon: sprintStateIcon(sprint?.state)?.icon,
        customIcon: true,
        customIconColor: sprintStateIcon(sprint?.state)?.color,
        customIconName: iconPath(sprintStateIcon(sprint?.state)?.icon),
        state: sprint.state,
      };
    });
    const noSprintsOption = {
      id: 'noSprints',
      label: translate('noSprintsLabel'),
      disabled: true,
    };

    return sprints.length ? sprints : [noSprintsOption];
  });

  return {
    sprintOptions,
    sortedSpaceSprints,
  };
}
